import { useState } from "react";
import { CancelButton } from "../../elements/CancelButton";
import { useGlobalReducer } from "../../../GlobalContext";
import { TextField } from "../../blocks/fields/TextField";
import { Button } from "../../elements/Button";
import { Loading } from "../../elements/Loading";
import { Modal } from "../../elements/Modal";
import { PlusIcon } from "../../icons";
import { Search } from "../../api/searchService";
import { Update } from "../../api/recordService";
import { isFailedResponse } from "../../api/request";
import { useEntityPage } from "../useEntityPage";
import {
  RelatedRecord,
  RelatedTableAddProps,
} from "../relatedTableActions.types";

interface ContactResult {
  Id: string;
  Name: string;
  Fields?: Record<string, unknown>;
}

/** As many as the legacy add-contacts search read in one go. */
const MAX_RESULTS = 200;

/**
 * Adds existing contacts to the account. Contacts are searched for, as the old account
 * form did, and only those not in an account already are offered.
 */
export function AddAccountContactAction({
  parentId,
  records,
  onAdded,
}: RelatedTableAddProps) {
  const globalDispatch = useGlobalReducer();
  const { record } = useEntityPage();
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({
    emailaddress1: "",
    firstname: "",
    lastname: "",
  });
  const [results, setResults] = useState<ContactResult[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeModal = () => {
    setOpen(false);
    setFilters({ emailaddress1: "", firstname: "", lastname: "" });
    setResults(null);
    setSelectedIds([]);
  };

  const hasFilter = Object.values(filters).some((value) => value.trim() !== "");

  const search = async () => {
    setSearching(true);
    setSelectedIds([]);

    const response = await Search(globalDispatch, "", {
      EntityName: "contact",
      Fields: null,
      Filters: {
        ...filters,
        searchfilter: "filteroutaccount",
        activeStatus: true,
        fieldOrderedBy: "fullname",
        fieldOrderedByAsc: true,
        fieldOrderedBySecondary: "",
        fieldOrderedBySecondaryAsc: false,
      },
      IsAdvancedSearch: false,
      AdvancedSearchJoins: [],
      AdvancedSearchNodes: null,
      PageRequested: 1,
      PageSize: MAX_RESULTS,
      IsExport: undefined,
      MarketingListId: undefined,
      MarketingListName: undefined,
      RemoveFromMarketingList: undefined,
      ReplaceExistingListMembers: undefined,
    });

    setResults(
      isFailedResponse(response)
        ? []
        : ((response?.data?.Records ?? []) as ContactResult[])
    );
    setSearching(false);
  };

  const toggle = (id: string) =>
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((selected) => selected !== id)
        : [...current, id]
    );

  // Through the account's own update, which links each contact to it; the service
  // wants the account's name with it.
  const handleAdd = async () => {
    const name = String(record?.name ?? "");
    setIsSubmitting(true);

    const response = await Update(
      globalDispatch,
      "Contacts added",
      {
        Id: parentId,
        Name: name,
        Attributes: {
          name,
          "accountcontact-recordstoadd": selectedIds.map((id) => ({ Id: id })),
        },
      },
      "account"
    );

    if (!isFailedResponse(response)) {
      const added: RelatedRecord[] = (results ?? [])
        .filter((contact) => selectedIds.includes(contact.Id))
        .map((contact) => ({
          Id: contact.Id,
          Name: contact.Name,
          Fields: {
            fullname: contact.Name,
            emailaddress1: contact.Fields?.emailaddress1 ?? "",
            createdon: contact.Fields?.createdon,
            modifiedon: contact.Fields?.modifiedon,
          },
        }));

      onAdded([...records, ...added]);
      closeModal();
    }

    setIsSubmitting(false);
  };

  const setFilter = (name: keyof typeof filters) => (value: unknown) =>
    setFilters((current) => ({ ...current, [name]: String(value ?? "") }));

  return (
    <>
      <Button
        variant="none"
        className="border rounded-2 bg-white text-dark fw-medium hover-bg-light d-flex align-items-center"
        icon={PlusIcon}
        text="Add"
        onClick={() => setOpen(true)}
      />
      {open && (
        <Modal title="Add contacts" onClose={closeModal} className="modal-lg">
          <div className="modal-body">
            <div className="row gy-2 align-items-end">
              <div className="col-12 col-md-4">
                <TextField
                  label="Email Address"
                  name="add-contact-email"
                  value={filters.emailaddress1}
                  onChange={setFilter("emailaddress1")}
                />
              </div>
              <div className="col-12 col-md-4">
                <TextField
                  label="First Name"
                  name="add-contact-firstname"
                  value={filters.firstname}
                  onChange={setFilter("firstname")}
                />
              </div>
              <div className="col-12 col-md-4">
                <TextField
                  label="Last Name"
                  name="add-contact-lastname"
                  value={filters.lastname}
                  onChange={setFilter("lastname")}
                />
              </div>
            </div>
            <p className="fs-7 fst-italic mt-2">
              Only contacts not in an account are shown. When searching by email
              address, the value must be an exact match.
            </p>
            <div className="d-flex justify-content-end mb-3">
              <Button
                text="Search"
                variant="primary"
                onClick={search}
                showSpinner={searching}
                disabled={!hasFilter || searching}
              />
            </div>
            {searching && <Loading />}
            {!searching && results?.length === 0 && (
              <p className="text-center mb-0">No contacts found.</p>
            )}
            {!searching && results && results.length > 0 && (
              <ul className="list-unstyled mb-0">
                {results.map((contact) => (
                  <li key={contact.Id} className="form-check">
                    <input
                      id={`add-contact-${contact.Id}`}
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedIds.includes(contact.Id)}
                      onChange={() => toggle(contact.Id)}
                    />
                    <label
                      htmlFor={`add-contact-${contact.Id}`}
                      className="form-check-label"
                    >
                      {contact.Name}
                      {contact.Fields?.emailaddress1
                        ? ` (${String(contact.Fields.emailaddress1)})`
                        : ""}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="modal-footer d-block">
            <div className="w-100 d-flex justify-content-between">
              <CancelButton
                onClick={closeModal}
                disabled={isSubmitting}
              />
              <Button
                text={
                  selectedIds.length > 1
                    ? `Add ${selectedIds.length} contacts`
                    : "Add"
                }
                variant="primary"
                onClick={handleAdd}
                showSpinner={isSubmitting}
                disabled={selectedIds.length === 0 || isSubmitting}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default AddAccountContactAction;
